import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../stores/siteStore';
import { workerAnalyticsAPI } from '../utils/api';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PERIODS = [
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: '90 Days', value: '90d' },
];

export default function WorkerAnalyticsPage() {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const [period, setPeriod] = useState('30d');
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [violatorSearch, setViolatorSearch] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const { data: performance, isLoading, isFetching } = useQuery({
    queryKey: ['worker-performance', siteId, period, customStartDate, customEndDate],
    queryFn: () => {
      const params = { siteId, period };
      if (period === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      return workerAnalyticsAPI.getWorkerPerformance(params).then(r => r.data);
    },
    enabled: Boolean(siteId && (period !== 'custom' || (customStartDate && customEndDate))),
  });

  const { data: checkIns } = useQuery({
    queryKey: ['worker-check-ins', siteId, period, customStartDate, customEndDate],
    queryFn: () => {
      const params = { siteId, period };
      if (period === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      return workerAnalyticsAPI.getWorkerCheckIns(params).then(r => r.data);
    },
    enabled: Boolean(siteId && (period !== 'custom' || (customStartDate && customEndDate))),
  });

  const summary = performance?.summary || {};
  const topViolators = performance?.topViolators || [];
  const scoreDistribution = performance?.scoreDistribution || {};
  const departmentBreakdown = performance?.departmentBreakdown || {};
  const tradeBreakdown = performance?.tradeBreakdown || {};

  // Filter violators based on search
  const filteredViolators = topViolators.filter(v => {
    const searchLower = violatorSearch.toLowerCase();
    const name = `${v.worker?.firstName || ''} ${v.worker?.lastName || ''}`.toLowerCase();
    const badge = v.worker?.badgeId?.toLowerCase() || '';
    const dept = v.worker?.department?.toLowerCase() || '';
    const trade = v.worker?.trade?.toLowerCase() || '';
    return (
      name.includes(searchLower) ||
      badge.includes(searchLower) ||
      dept.includes(searchLower) ||
      trade.includes(searchLower)
    );
  });

  // CSV export function
  const escapeCSVField = (field) => {
    const value = String(field ?? '');
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExport = () => {
    try {
      const headers = ['Rank', 'Name', 'Badge', 'Department', 'Trade', 'Violations', 'Safety Score', 'Contractor'];
      const data = topViolators.map((v, i) => [
        i + 1,
        `${v.worker?.firstName || ''} ${v.worker?.lastName || ''}`,
        v.worker?.badgeId || '-',
        v.worker?.department || '-',
        v.worker?.trade || '-',
        v.violationCount,
        v.worker?.safetyScore || '-',
        v.worker?.contractor?.name || '-',
      ]);

      const csv = [
        headers.map(escapeCSVField).join(','),
        ...data.map(row => row.map(escapeCSVField).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `worker-analytics-${period}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Worker Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Safety performance and compliance by worker</p>
        </div>
        <button
          onClick={handleExport}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium w-fit"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Period & Date Selection */}
      <div className="flex">
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => {
                  setPeriod(p.value);
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p.value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() => setPeriod('custom')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                period === 'custom' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Custom
            </button>
          </div>
          {period === 'custom' && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2 py-1 text-xs bg-white border border-slate-300 rounded"
              />
              <span className="text-xs text-slate-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2 py-1 text-xs bg-white border border-slate-300 rounded"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-300 ${isFetching && !isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.totalWorkers || 0}</p>
              <p className="text-xs text-slate-500">Total Workers</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.avgSafetyScore || 100}</p>
              <p className="text-xs text-slate-500">Avg Safety Score</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.totalViolations || 0}</p>
              <p className="text-xs text-slate-500">Total Violations</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.workersWithViolations || 0}</p>
              <p className="text-xs text-slate-500">Workers with Violations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Safety Score Distribution */}
        <div className="glass p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Safety Score Distribution</h3>
          <div className="space-y-2">
            {Object.entries(scoreDistribution).map(([range, count]) => {
              const totalWorkers = Object.values(scoreDistribution).reduce((a, b) => a + b, 0);
              const pct = totalWorkers > 0 ? (count / totalWorkers) * 100 : 0;
              const color = range.includes('90') ? 'bg-emerald-500' :
                           range.includes('80') ? 'bg-blue-500' :
                           range.includes('70') ? 'bg-amber-500' :
                           range.includes('60') ? 'bg-orange-500' : 'bg-red-500';
              return (
                <div key={range} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-600 w-20">{range}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Check-in Stats */}
        <div className="glass p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Check-in Patterns</h3>
          {checkIns ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-800">{checkIns.totalCheckIns}</p>
                  <p className="text-[10px] text-slate-500">Total Check-ins</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-800">{checkIns.avgSessionDuration}m</p>
                  <p className="text-[10px] text-slate-500">Avg Session</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-800">{checkIns.ppeComplianceAtEntry}%</p>
                  <p className="text-[10px] text-slate-500">PPE at Entry</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Peak Hours</p>
                <div className="flex gap-2 flex-wrap">
                  {(checkIns.peakHours || []).map(ph => (
                    <span key={ph.hour} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg">
                      {ph.hour}:00 ({ph.count})
                    </span>
                  ))}
                </div>
              </div>
              {/* Hourly distribution mini-chart */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Hourly Distribution</p>
                <div className="flex items-end gap-px h-16">
                  {(checkIns.hourlyDistribution || []).map((count, hour) => {
                    const maxH = Math.max(...(checkIns.hourlyDistribution || [1]));
                    const h = maxH > 0 ? (count / maxH) * 100 : 0;
                    return (
                      <div
                        key={hour}
                        className="flex-1 bg-blue-400 rounded-t-sm transition-all hover:bg-blue-500"
                        style={{ height: `${Math.max(2, h)}%` }}
                        title={`${hour}:00 - ${count} check-ins`}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between text-[8px] text-slate-400 mt-0.5">
                  <span>0:00</span><span>6:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center mt-8">No check-in data</p>
          )}
        </div>
      </div>

      {/* Top Violators Table */}
      <div className="glass p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">Top Violators</h3>
          <div className="relative w-48">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, badge, dept..."
              value={violatorSearch}
              onChange={(e) => setViolatorSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Rank</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Worker</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Badge</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Department</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Trade</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Violations</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Safety Score</th>
              </tr>
            </thead>
            <tbody>
              {filteredViolators.map((v, i) => (
                <tr
                  key={v.workerId}
                  className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedWorker(v)}
                >
                  <td className="py-2 px-3 font-bold text-slate-400">#{i + 1}</td>
                  <td className="py-2 px-3 font-medium text-slate-700">
                    {v.worker ? `${v.worker.firstName} ${v.worker.lastName}` : v.workerId?.slice(0, 8)}
                  </td>
                  <td className="py-2 px-3 text-slate-500">{v.worker?.badgeId || '-'}</td>
                  <td className="py-2 px-3 text-slate-500">{v.worker?.department || '-'}</td>
                  <td className="py-2 px-3 text-slate-500">{v.worker?.trade || '-'}</td>
                  <td className="py-2 px-3 text-right">
                    <span className={`font-bold ${v.violationCount > 10 ? 'text-red-600' : v.violationCount > 5 ? 'text-amber-600' : 'text-slate-700'}`}>
                      {v.violationCount}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`font-bold ${
                      (v.worker?.safetyScore || 100) >= 90 ? 'text-emerald-600' :
                      (v.worker?.safetyScore || 100) >= 70 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {v.worker?.safetyScore || '-'}
                    </span>
                  </td>
                </tr>
              ))}
              {filteredViolators.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    {violatorSearch ? 'No workers match your search' : 'No violation data for this period'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Department & Trade Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Department Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(departmentBreakdown).map(([dept, data]) => (
              <div key={dept} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50/50">
                <div>
                  <p className="text-sm font-medium text-slate-700">{dept}</p>
                  <p className="text-xs text-slate-500">{data.workers} workers</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-700">{data.avgScore}</p>
                  <p className="text-xs text-slate-500">{data.totalViolations} violations</p>
                </div>
              </div>
            ))}
            {Object.keys(departmentBreakdown).length === 0 && (
              <p className="text-sm text-slate-400 text-center">No department data</p>
            )}
          </div>
        </div>

        <div className="glass p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Trade Analysis</h3>
          <div className="space-y-3">
            {Object.entries(tradeBreakdown).map(([trade, data]) => (
              <div key={trade} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50/50">
                <div>
                  <p className="text-sm font-medium text-slate-700">{trade}</p>
                  <p className="text-xs text-slate-500">{data.workers} workers</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-700">{data.avgScore}</p>
                  <p className="text-xs text-slate-500">{data.totalViolations} violations</p>
                </div>
              </div>
            ))}
            {Object.keys(tradeBreakdown).length === 0 && (
              <p className="text-sm text-slate-400 text-center">No trade data</p>
            )}
          </div>
        </div>
      </div>

      {/* Worker Detail Modal */}
      {selectedWorker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setSelectedWorker(null)}>
          <div className="glass p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Worker Detail</h3>
              <button onClick={() => setSelectedWorker(null)} className="icon-btn">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-lg font-bold text-slate-600">
                  {selectedWorker.worker?.firstName?.[0]}{selectedWorker.worker?.lastName?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">
                    {selectedWorker.worker ? `${selectedWorker.worker.firstName} ${selectedWorker.worker.lastName}` : 'Unknown Worker'}
                  </p>
                  <p className="text-xs text-slate-500">Badge: {selectedWorker.worker?.badgeId || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200">
                <div>
                  <p className="text-xs text-slate-500">Department</p>
                  <p className="font-medium text-slate-700">{selectedWorker.worker?.department || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Trade</p>
                  <p className="font-medium text-slate-700">{selectedWorker.worker?.trade || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Safety Score</p>
                  <p className={`font-bold ${(selectedWorker.worker?.safetyScore || 100) >= 80 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {selectedWorker.worker?.safetyScore || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Violations (period)</p>
                  <p className="font-bold text-slate-700">{selectedWorker.violationCount}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Contractor</p>
                  <p className="font-medium text-slate-700">{selectedWorker.worker?.contractor?.name || 'Direct Employee'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
